import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type { YogiQLNames } from 'features/charts-v2/utils/to-yogiql-chart-request'
import type { FilterOption, FilterOptionValue } from 'features/filters/types'

/**
 * The theme and dataset filter values a panel holds are cluster / project
 * uuids; YogiQL takes names and titles. Resolve what the current filter
 * options can; an unresolved uuid is simply absent and the serialiser then
 * sends that panel the legacy way. A grouped brand breakdown also carries the
 * hub's brands split by ownership (the brand option's `customer_brands`), so
 * the adapter can rebuild the drill-down's `category_mapping`; a grouped
 * theme breakdown carries each parent theme's member names the same way.
 */
export function resolveYogiQLNames(
  panel: ChartPanel,
  filterOptions: FilterOption[] | undefined,
): YogiQLNames {
  const names: YogiQLNames = { themes: {}, datasets: {} }
  const optionValues = (field: string): FilterOptionValue[] => {
    const option = filterOptions?.find((o) => o.field === field)
    return option && 'values' in option ? option.values : []
  }
  const grouped = (id: string): boolean =>
    panel.breakdowns.some(
      (b) =>
        b.kind === 'standard' && b.id === id && 'grouped' in b && b.grouped,
    )
  if (grouped('brand')) {
    const option = filterOptions?.find((o) => o.field === 'brand')
    const owned = new Set(
      option && 'customer_brands' in option
        ? (option.customer_brands ?? [])
        : [],
    )
    const all = optionValues('brand').map((v) => v.value)
    names.brandOwnership = {
      mine: all.filter((b) => owned.has(b)),
      competitor: all.filter((b) => !owned.has(b)),
    }
  }
  if (grouped('theme')) {
    // Theme names are `Parent: Child`; the parent is the text before the
    // first colon, untrimmed, as YogiQL's parent_theme values are.
    const parents: Record<string, string[]> = {}
    for (const value of optionValues('theme')) {
      const parent = value.value.split(':', 1)[0] ?? value.value
      ;(parents[parent] ??= []).push(value.value)
    }
    names.themeParents = parents
  }
  for (const filter of panel.filters) {
    if (filter.field === 'theme') {
      for (const value of optionValues('theme')) {
        const ids = [value.cluster_uuid, ...(value.identifiers ?? [])]
        for (const id of ids) {
          if (id && filter.values.includes(id)) names.themes[id] = value.value
        }
      }
    }
    if (filter.field === 'proj_uuid' || filter.field === 'dataset') {
      for (const value of optionValues('proj_uuid')) {
        const ids = [
          value.proj_uuid,
          value.cluster_uuid,
          ...(value.identifiers ?? []),
        ]
        for (const id of ids) {
          if (id && filter.values.includes(id)) names.datasets[id] = value.value
        }
      }
    }
  }
  return names
}
